import React, { useEffect, useMemo, useState } from 'react';

import useAuthStore from '@entities/auth/model/useAuthStore';
import {
  useEmployeeInfosQuery,
  useGetEmployeeByNameQuery,
  useGetEmployeesByTeamQuery,
  useGetEmployeesByCompanyQuery,
} from '@features/employee/hooks';
import useGetTeamQuery from '@features/team/hooks';
import { useSignLineInfoQuery, useSignLineListQuery } from '@features/signLine/hooks';
import { useDocFormCheckEmpQuery } from '@features/form-selection/hooks';
import Alert from '@shared/components/atoms/Alert';

import OrganizationTree from '../OrganizationTree/OrganizationTree';
import { sortOrganizationTree } from '@features/document-write/utils/sortOrganizationTree';

import styles from './ApprovalLineModal.module.scss';

/**
 * ApprovalLineModal 컴포넌트
 * - 결재선 선택 모달
 * - 피그마 디자인 기반: 왼쪽 조직도 트리, 오른쪽 직원 검색 및 선택
 */
function ApprovalLineModal({
  isOpen,
  onClose,
  onSelect,
  approvalLine = null,
  formId = null,
  isApprovalLineModified = false,
}) {
  const [selectedTeamCode, setSelectedTeamCode] = useState(null);
  const [isCompanySelected, setIsCompanySelected] = useState(false); // 회사(최상위 조직) 선택 상태
  const [searchName, setSearchName] = useState('');
  const [searchTrigger, setSearchTrigger] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [approvalLineName, setApprovalLineName] = useState('선택안함');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null); // 드래그 오버 중인 인덱스
  const [dragOverPosition, setDragOverPosition] = useState(null); // 'top' | 'bottom'
  const [checkedEmployees, setCheckedEmployees] = useState(new Set());
  const [selectedSignLineId, setSelectedSignLineId] = useState(null);
  const [alertState, setAlertState] = useState({
    isOpen: false,
    message: '',
  });

  const MAX_SELECTED_EMPLOYEES = 9;

  // 모달 오픈 시 배경(body) 스크롤 방지
  useEffect(
    function () {
      if (!isOpen) {
        return undefined;
      }

      const currentCount = Number(document.body.dataset.modalLockCount || '0');
      const nextCount = currentCount + 1;
      document.body.dataset.modalLockCount = String(nextCount);

      if (currentCount === 0) {
        document.body.dataset.modalLockOverflow = document.body.style.overflow || '';
        document.body.dataset.modalLockPaddingRight = document.body.style.paddingRight || '';

        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollBarWidth > 0) {
          document.body.style.paddingRight = `${scrollBarWidth}px`;
        }
      }

      return function () {
        const count = Number(document.body.dataset.modalLockCount || '0');
        const restoredCount = Math.max(0, count - 1);
        document.body.dataset.modalLockCount = String(restoredCount);

        if (restoredCount === 0) {
          document.body.style.overflow = document.body.dataset.modalLockOverflow || '';
          document.body.style.paddingRight = document.body.dataset.modalLockPaddingRight || '';
          delete document.body.dataset.modalLockOverflow;
          delete document.body.dataset.modalLockPaddingRight;
        }
      };
    },
    [isOpen],
  );

  // Alert 표시 함수
  function showAlert(message) {
    setAlertState({
      isOpen: true,
      message,
    });
  }

  // Alert 닫기 함수
  function closeAlert() {
    setAlertState({
      isOpen: false,
      message: '',
    });
  }

  // 회사 코드 가져오기 (Zustand store에서)
  const companyCode = useAuthStore((state) => state.companyCode);

  // 팀 목록 조회 (모달이 열릴 때만 호출)
  const teamQuery = useGetTeamQuery(companyCode, {
    enabled: isOpen && !!companyCode,
  });
  const teamList = teamQuery.data?.resultData?.teamList || [];

  // 팀별 사원 목록 조회 (모달이 열릴 때만 호출)
  const employeeQuery = useGetEmployeesByTeamQuery(selectedTeamCode, {
    enabled: isOpen && !!selectedTeamCode && !isCompanySelected,
  });
  const teamEmployees = employeeQuery.data?.resultData?.employeeList || [];

  // 회사(최상위 조직) 선택 시 대표이사 조회
  const companyEmployeeQuery = useGetEmployeesByCompanyQuery(companyCode, {
    enabled: isOpen && isCompanySelected && !!companyCode,
  });
  const companyEmployeesRaw = companyEmployeeQuery.data?.resultData?.employeeList || [];
  // 대표이사만 필터링 (직책이 "대표이사"인 직원만)
  const companyEmployees = companyEmployeesRaw.filter(function (emp) {
    return emp.position === '대표이사' || emp.position === '대표';
  });

  // 현재 표시할 직원 목록 (회사 선택 시 대표이사, 팀 선택 시 팀원)
  const employees = isCompanySelected ? companyEmployees : teamEmployees;

  // 사원 이름 검색 (모달이 열릴 때만 호출)
  const employeeNameQuery = useGetEmployeeByNameQuery(searchTrigger, {
    enabled: isOpen && searchTrigger.length >= 1,
  });
  const searchResults = employeeNameQuery.data?.resultData?.employeeList || [];
  const isSearchLoading = employeeNameQuery.isLoading;

  // 자주 쓰는 결재라인 목록 조회
  const signLineListQuery = useSignLineListQuery({
    enabled: isOpen,
  });
  const signLineList = signLineListQuery.data?.resultData?.signLineList || [];

  // 선택된 결재라인 상세 정보 조회
  const signLineInfoQuery = useSignLineInfoQuery(selectedSignLineId, {
    enabled: isOpen && !!selectedSignLineId,
  });

  // 양식 검토자 정보 조회 (자주 쓰는 결재라인이 선택되지 않았을 때만)
  const { data: formCheckEmpData } = useDocFormCheckEmpQuery(formId, {
    enabled: isOpen && !!formId && !selectedSignLineId,
  });

  // 검토자 사원번호 목록 추출 (check1~4 중 -1이 아닌 값만)
  const formCheckEmpMembers = useMemo(
    function () {
      if (!formCheckEmpData || selectedSignLineId) {
        return [];
      }
      const members = [];
      for (let i = 1; i <= 4; i++) {
        const checkEmp = formCheckEmpData[`check${i}`];
        if (checkEmp && checkEmp !== -1) {
          members.push({ enumber: checkEmp });
        }
      }
      return members;
    },
    [formCheckEmpData, selectedSignLineId],
  );

  // 양식 검토자 직원 정보 조회
  const formCheckEmpInfosResults = useEmployeeInfosQuery(formCheckEmpMembers, {
    enabled: formCheckEmpMembers.length > 0 && !selectedSignLineId,
  });

  // 모달이 열릴 때 approvalLine을 기반으로 selectedEmployees 초기화
  // 단, 자주 쓰는 결재라인이 선택된 경우는 제외 (자주 쓰는 결재라인이 우선)
  useEffect(
    function () {
      // 자주 쓰는 결재라인이 선택된 경우는 이 useEffect를 실행하지 않음
      // (자주 쓰는 결재라인 useEffect가 처리함)
      // approvalLine이 변경되어도 자주 쓰는 결재라인이 선택되어 있으면 실행하지 않음
      if (selectedSignLineId) {
        return;
      }

      if (isOpen && approvalLine) {
        const employees = [];

        // approvalLine 슬롯에서 role + order를 보존해서 복원합니다.
        for (let i = 1; i <= 4; i++) {
          const approver = approvalLine[`approval${i}`];
          if (approver && (approver.name || approver.enumber)) {
            employees.push({
              enumber: approver.enumber || 0,
              name: approver.name || '',
              position: approver.position || '',
              team: approver.team || '',
              role: '결재',
              order: approver.order || 0,
            });
          }
        }

        for (let i = 1; i <= 4; i++) {
          const reviewer = approvalLine[`review${i}`];
          if (reviewer && (reviewer.name || reviewer.enumber)) {
            employees.push({
              enumber: reviewer.enumber || 0,
              name: reviewer.name || '',
              position: reviewer.position || '',
              team: reviewer.team || '',
              role: '검토',
              order: reviewer.order || 0,
            });
          }
        }

        const agreement = approvalLine.agreement1;
        if (agreement && (agreement.name || agreement.enumber)) {
          employees.push({
            enumber: agreement.enumber || 0,
            name: agreement.name || '',
            position: agreement.position || '',
            team: agreement.team || '',
            role: '합의',
            order: agreement.order || 0,
          });
        }

        // 사용자 지정 순서(order) 그대로 유지. 정렬 없이 order 순서대로만 복원.
        const byOrder = [];
        employees.forEach(function (e) {
          const o = Number(e.order) || 0;
          if (o > 0) byOrder[o] = e;
        });
        const inOrder = [];
        for (let ord = 2; ord < byOrder.length; ord += 1) {
          if (byOrder[ord]) inOrder.push(byOrder[ord]);
        }
        setSelectedEmployees(inOrder.length ? inOrder : employees);
      } else if (!isOpen) {
        // 모달이 닫힐 때 초기화
        setSelectedEmployees([]);
        setCheckedEmployees(new Set());
        setSearchName('');
        setSearchTrigger('');
        setSelectedTeamCode(null);
        setIsCompanySelected(false);
        setSelectedSignLineId(null);
      }
    },
    [isOpen, approvalLine, selectedSignLineId],
  );

  // 양식 검토자 정보를 모달에 추가 (결재라인이 비어 있을 때만, 사용자가 제거한 경우 재추가하지 않음)
  useEffect(
    function () {
      // 자주 쓰는 결재라인이 선택된 경우는 양식 검토자를 추가하지 않음
      // 사용자가 결재라인을 한 번이라도 수정했다면 양식 기본 검토자를 재주입하지 않음
      if (!isOpen || selectedSignLineId || isApprovalLineModified) return;
      if (!formCheckEmpData || formCheckEmpMembers.length === 0) return;
      if (!formCheckEmpInfosResults.every((r) => r.isSuccess || r.isError)) return;

      // approvalLine에 검토자/결재자/합의자가 하나라도 있으면 양식 검토자를 추가하지 않음
      // (사용자가 검토자만 제거한 경우에도 결재자가 있으면 재추가하지 않음)
      const hasExistingReviewers = [1, 2, 3, 4].some(function (i) {
        return approvalLine?.[`review${i}`]?.enumber;
      });
      const hasExistingApprovers = [1, 2, 3, 4].some(function (i) {
        return approvalLine?.[`approval${i}`]?.enumber;
      });
      const hasExistingAgreement = !!approvalLine?.agreement1?.enumber;

      if (hasExistingReviewers || hasExistingApprovers || hasExistingAgreement) return;

      // 직원 정보 맵 생성
      const employeeInfoMap = new Map();
      formCheckEmpInfosResults.forEach(function (result, index) {
        const enumber = formCheckEmpMembers[index]?.enumber;
        if (enumber && result.data?.resultData) {
          const employeeData = result.data.resultData.employee || result.data.resultData;
          employeeInfoMap.set(enumber, employeeData);
        }
      });

      // 양식 검토자를 selectedEmployees에 추가 (역할: 검토, 순번: 1, 2, 3, 4)
      setSelectedEmployees(function (prev) {
        // 기존에 같은 enumber가 있는지 확인
        const existingEnumbers = new Set(prev.map((e) => e.enumber));

        const newReviewers = [];
        for (let i = 1; i <= 4; i++) {
          const checkEmp = formCheckEmpData[`check${i}`];
          if (checkEmp && checkEmp !== -1 && !existingEnumbers.has(checkEmp)) {
            const employeeInfo = employeeInfoMap.get(checkEmp);
            if (employeeInfo) {
              newReviewers.push({
                enumber: checkEmp,
                name: employeeInfo.name || '',
                position: employeeInfo.position || '',
                team: employeeInfo.team || '',
                role: '검토',
                order: 0, // 순번은 나중에 드래그로 조정 가능
              });
            }
          }
        }

        if (newReviewers.length > 0) {
          return [...prev, ...newReviewers];
        }

        return prev;
      });
    },
    [
      isOpen,
      formCheckEmpData,
      formCheckEmpMembers,
      formCheckEmpInfosResults,
      selectedSignLineId,
      isApprovalLineModified,
      approvalLine,
    ],
  );

  const sortedTeamList = useMemo(
    function () {
      return sortOrganizationTree(teamList);
    },
    [teamList],
  );

  // 팀 선택 핸들러
  function handleTeamSelect(teamCode) {
    setSelectedTeamCode(teamCode);
    setIsCompanySelected(false); // 팀 선택 시 회사 선택 해제
    setSearchName('');
    setSearchTrigger('');
  }

  // 회사(최상위 조직) 선택 핸들러
  function handleCompanySelect() {
    setSelectedTeamCode(null); // 팀 선택 해제
    setIsCompanySelected(true);
    setSearchName('');
    setSearchTrigger('');
  }

  // 역할별 인원수 제한 확인
  function checkRoleLimit(role, currentCount) {
    const limits = {
      결재: 4,
      검토: 4,
      합의: 1,
    };
    return currentCount >= limits[role];
  }

  // 중복 직원 확인 (기안자 포함)
  function checkDuplicateEmployee(employee) {
    // selectedEmployees에서 중복 확인
    const isInSelected = selectedEmployees.some((emp) => emp.enumber === employee.enumber);
    if (isInSelected) {
      return true;
    }

    // approvalLine에서 기안자 확인 (draft1)
    if (approvalLine?.draft1) {
      // draft1에 enumber가 없을 수도 있으므로 name으로도 확인
      if (approvalLine.draft1.enumber === employee.enumber) {
        return true;
      }
      if (approvalLine.draft1.name === employee.name) {
        return true;
      }
    }

    return false;
  }

  // 선택 인원수 제한 확인 (9명)
  function checkMaxSelected(nextAddCount = 1) {
    if (selectedEmployees.length + nextAddCount > MAX_SELECTED_EMPLOYEES) {
      showAlert('9명 이상 추가할 수 없습니다.');
      return false;
    }
    return true;
  }

  // 직원 추가 핸들러 (단일)
  function handleAddEmployee(employee, role = '선택') {
    // 최대 9명 제한
    if (!checkMaxSelected(1)) {
      return;
    }

    // 중복 확인만 수행 (역할별 인원수 검증은 제거)
    if (checkDuplicateEmployee(employee)) {
      showAlert('선택한 사원이 이미 결재라인에 존재합니다.');
      return;
    }

    const newEmployee = {
      enumber: employee.enumber,
      name: employee.name,
      position: employee.position || '',
      team: employee.team || '',
      role, // 기본값: '선택'
    };
    setSelectedEmployees([...selectedEmployees, newEmployee]);
    // 체크박스도 체크 상태로 업데이트
    setCheckedEmployees(function (prev) {
      const newChecked = new Set(prev);
      newChecked.add(employee.enumber);
      return newChecked;
    });
  }

  // 체크박스 토글 - 체크 시 즉시 결재자 테이블에 추가/제거
  function handleCheckboxToggle(employee) {
    const isChecked = checkedEmployees.has(employee.enumber);
    const isInSelected = selectedEmployees.some((emp) => emp.enumber === employee.enumber);

    if (isChecked) {
      // 체크 해제: 결재자 테이블에서 제거
      setCheckedEmployees(function (prev) {
        const newChecked = new Set(prev);
        newChecked.delete(employee.enumber);
        return newChecked;
      });
      if (isInSelected) {
        setSelectedEmployees(function (prev) {
          return prev.filter((emp) => emp.enumber !== employee.enumber);
        });
      }
    } else {
      // 체크: 결재자 테이블에 추가
      // 이미 selectedEmployees에 있으면 추가하지 않음 (중복 방지)
      if (isInSelected) {
        // 체크박스만 체크 상태로 변경
        setCheckedEmployees(function (prev) {
          const newChecked = new Set(prev);
          newChecked.add(employee.enumber);
          return newChecked;
        });
        return;
      }

      // 최대 9명 제한 확인
      if (!checkMaxSelected(1)) {
        return;
      }

      // 중복 확인 (기안자 포함)
      if (checkDuplicateEmployee(employee)) {
        showAlert('선택한 사원이 이미 결재라인에 존재합니다.');
        return;
      }

      const newEmployee = {
        enumber: employee.enumber,
        name: employee.name,
        position: employee.position || '',
        team: employee.team || '',
        role: '선택',
      };

      setCheckedEmployees(function (prev) {
        const newChecked = new Set(prev);
        newChecked.add(employee.enumber);
        return newChecked;
      });
      setSelectedEmployees(function (prev) {
        // 중복 확인을 한 번 더 수행 (상태 업데이트가 비동기적이므로)
        const alreadyExists = prev.some((emp) => emp.enumber === employee.enumber);
        if (alreadyExists) {
          return prev;
        }
        return [...prev, newEmployee];
      });
    }
  }

  // 자주 쓰는 결재라인 선택 핸들러
  function handleSignLineSelect(signLineId) {
    if (!signLineId || signLineId === '') {
      setSelectedSignLineId(null);
      return;
    }
    setSelectedSignLineId(signLineId);
  }

  // 선택된 결재라인 정보를 selectedEmployees에 반영
  const signLineInfo = useMemo(
    function () {
      return signLineInfoQuery.data?.resultData;
    },
    [signLineInfoQuery.data],
  );

  // 자주 쓰는 결재라인의 사원 번호 목록 추출
  // sign1은 기안자(사용자)로 항상 기본값이므로 제외하고 sign2부터 추출
  const signLineMembers = useMemo(
    function () {
      if (!selectedSignLineId || !signLineInfo) {
        return [];
      }

      const members = [];
      // sign1 제외, sign2~sign5까지 추출
      for (let i = 2; i <= 5; i++) {
        if (signLineInfo[`sign${i}`]) {
          members.push({ enumber: signLineInfo[`sign${i}`] });
        }
      }
      for (let i = 1; i <= 4; i++) {
        if (signLineInfo[`check${i}`]) {
          members.push({ enumber: signLineInfo[`check${i}`] });
        }
      }
      if (signLineInfo.agreement) {
        members.push({ enumber: signLineInfo.agreement });
      }

      return members;
    },
    [selectedSignLineId, signLineInfo],
  );

  // 사원 번호 목록으로 사원 정보들을 한번에 조회
  const employeeInfosResults = useEmployeeInfosQuery(signLineMembers, {
    enabled: signLineMembers.length > 0,
  });

  // 의존성 배열에 배열 참조(employeeInfosResults)를 넣으면 매 렌더마다 새 참조로 인해
  // useEffect 무한 실행 → setState → 리렌더 → Maximum update depth exceeded 발생.
  // "준비 완료 여부"만 불리언으로 의존하도록 함.
  const isEmployeeInfosReady =
    employeeInfosResults.length > 0 && employeeInfosResults.every((r) => r.isSuccess);

  useEffect(
    function () {
      if (!selectedSignLineId || !signLineInfo || !isEmployeeInfosReady) {
        return;
      }

      const employeeInfoMap = new Map(
        employeeInfosResults.map(function (r, index) {
          const requestEnumber = signLineMembers[index]?.enumber;
          const employeeData = r.data?.resultData?.employee || r.data?.resultData;
          return [requestEnumber, employeeData];
        }),
      );

      const employees = [];
      const addedEnumbers = new Set(); // 동일 인물(enumber) 중복 추가 방지

      // 결재자 정보 추출 (sign1은 기안자 제외, sign2~sign5)
      for (let i = 2; i <= 5; i++) {
        const enumber = signLineInfo[`sign${i}`];
        if (enumber && !addedEnumbers.has(enumber)) {
          addedEnumbers.add(enumber);
          const info = employeeInfoMap.get(enumber);
          employees.push({
            enumber,
            name: info?.name || '',
            position: info?.position || '',
            team: info?.team || '',
            role: '결재',
            order: signLineInfo[`sign${i}_order`] || 0,
          });
        }
      }

      // 검토자 정보 추출
      for (let i = 1; i <= 4; i++) {
        const enumber = signLineInfo[`check${i}`];
        if (enumber && !addedEnumbers.has(enumber)) {
          addedEnumbers.add(enumber);
          const info = employeeInfoMap.get(enumber);
          employees.push({
            enumber,
            name: info?.name || '',
            position: info?.position || '',
            team: info?.team || '',
            role: '검토',
            order: signLineInfo[`check${i}_order`] || 0,
          });
        }
      }

      // 합의자 정보 추출
      const agreementEnumber = signLineInfo.agreement;
      if (agreementEnumber && !addedEnumbers.has(agreementEnumber)) {
        addedEnumbers.add(agreementEnumber);
        const info = employeeInfoMap.get(agreementEnumber);
        employees.push({
          enumber: agreementEnumber,
          name: info?.name || '',
          position: info?.position || '',
          team: info?.team || '',
          role: '합의',
          order: signLineInfo.agreement_order || 0,
        });
      }

      // 사용자 지정 순서(order 2, 3, 4, …) 그대로 복원. 정렬 없이 order 순서대로만 나열.
      const byOrder = [];
      employees.forEach(function (e) {
        const o = Number(e.order) || 0;
        if (o > 0) byOrder[o] = e;
      });
      const inOrder = [];
      for (let ord = 2; ord < byOrder.length; ord += 1) {
        if (byOrder[ord]) inOrder.push(byOrder[ord]);
      }
      setSelectedEmployees(inOrder.length ? inOrder : employees);
    },
    [selectedSignLineId, signLineInfo, isEmployeeInfosReady],
  );

  // 직원 리스트에서 드래그 시작
  function handleEmployeeDragStart(e, employee) {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify(employee));
    e.target.style.opacity = '0.5';
  }

  // 직원 리스트에서 드래그 종료
  function handleEmployeeDragEnd(e) {
    e.target.style.opacity = '1';
  }

  // 승인 테이블 영역에 드롭
  function handleDropToSelectedSection(e) {
    e.preventDefault();
    const employeeData = e.dataTransfer.getData('application/json');
    if (!employeeData) {
      return;
    }

    try {
      const employee = JSON.parse(employeeData);
      // 드래그 앤 드롭 시 기본 역할은 '선택'
      handleAddEmployee(employee, '선택');
    } catch (error) {
      console.error('Failed to parse employee data:', error);
    }
  }

  // 선택된 직원 제거
  function handleRemoveEmployee(index) {
    const employeeToRemove = selectedEmployees[index];
    setSelectedEmployees(function (prev) {
      return prev.filter((_, i) => i !== index);
    });
    // 체크박스에서도 해제
    setCheckedEmployees(function (prev) {
      const newChecked = new Set(prev);
      newChecked.delete(employeeToRemove.enumber);
      return newChecked;
    });
  }

  // 역할 변경
  function handleRoleChange(index, newRole) {
    const updated = [...selectedEmployees];
    const currentEmployee = updated[index];
    const currentRole = currentEmployee.role;

    // '선택'으로 변경하는 경우는 검증하지 않음
    if (newRole === '선택') {
      updated[index].role = newRole;
      setSelectedEmployees(updated);
      return;
    }

    // 다른 역할로 변경할 때만 인원수 확인 (현재 직원 제외)
    const newRoleCount = updated.filter(
      (emp) => emp.role === newRole && emp !== currentEmployee,
    ).length;
    if (checkRoleLimit(newRole, newRoleCount)) {
      showAlert(`${newRole}는 ${newRole === '합의' ? '1명' : '4명'}까지만 선택이 가능합니다.`);
      return;
    }

    updated[index].role = newRole;
    setSelectedEmployees(updated);
  }

  // 드래그 시작
  function handleDragStart(e, index) {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  }

  // 드래그 종료
  function handleDragEnd(e) {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 드래그 오버 (드롭 가능 영역 표시)
  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  // 카드 위에서 드래그 오버 (위/아래 구분)
  function handleCardDragOver(e, index) {
    e.preventDefault();
    e.stopPropagation();

    // 드래그 중이 아니면 무시
    if (draggedIndex === null) {
      return;
    }

    // 같은 카드 위에서는 무시
    if (draggedIndex === index) {
      setDragOverIndex(null);
      setDragOverPosition(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const cardMiddle = rect.top + rect.height / 2;

    // 마우스가 카드의 위쪽 절반에 있으면 'top', 아래쪽 절반에 있으면 'bottom'
    const position = mouseY < cardMiddle ? 'top' : 'bottom';

    setDragOverIndex(index);
    setDragOverPosition(position);
  }

  // 카드에서 드래그 리브 (드래그가 카드 밖으로 나갔을 때)
  function handleCardDragLeave(e) {
    // 자식 요소로 이동한 경우는 무시
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 드롭 (순서 변경)
  function handleDrop(e, dropIndex) {
    e.preventDefault();
    if (draggedIndex === null) {
      return;
    }

    // 같은 위치에 드롭하면 무시
    if (draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDragOverPosition(null);
      return;
    }

    const updated = [...selectedEmployees];
    const draggedItem = updated[draggedIndex];

    // 드래그된 아이템 제거
    updated.splice(draggedIndex, 1);

    // dragOverPosition에 따라 삽입 위치 결정
    let insertIndex = dropIndex;
    if (dragOverPosition === 'bottom') {
      // 아래에 드롭: dropIndex 다음 위치에 삽입
      insertIndex = dropIndex + 1;
    }
    // 위에 드롭: dropIndex 위치에 삽입 (이미 insertIndex = dropIndex)

    // 드롭 위치에 삽입
    updated.splice(insertIndex, 0, draggedItem);

    setSelectedEmployees(updated);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 검색 핸들러
  function handleSearch() {
    setSearchTrigger(searchName);
    setSelectedTeamCode(null);
  }

  // 저장 핸들러
  function handleSave() {
    // '선택'인 직원이 있는지 확인
    const unselectedEmployees = selectedEmployees.filter((emp) => emp.role === '선택');
    if (unselectedEmployees.length > 0) {
      showAlert('역할이 지정되지 않은 직원이 있습니다. 모든 직원의 역할을 선택해주세요.');
      return;
    }

    if (onSelect) {
      // 모든 선택된 직원을 역할별로 분류하여 반환 (빈 배열도 전달)
      onSelect(selectedEmployees);
    }
    onClose();
  }

  // 검색 결과 표시 여부
  const showSearchResults = searchTrigger.length >= 1;
  const displayEmployees = showSearchResults ? searchResults : employees;

  // 모달이 닫혀있으면 렌더링하지 않음
  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} role="dialog" aria-modal="true">
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>결재자 추가</h2>
        </div>

        {/* 자주 쓰는 결재라인 선택 */}
        <div className={styles.signLineSection}>
          <div className={styles.signLineLabel}>자주 쓰는 결재라인</div>
          <select
            className={styles.signLineDropdown}
            value={selectedSignLineId || ''}
            onChange={function (e) {
              handleSignLineSelect(e.target.value || null);
            }}
          >
            <option value="">선택</option>
            {signLineList.map(function (signLine) {
              return (
                <option key={signLine.idx} value={signLine.idx}>
                  {signLine.title}
                </option>
              );
            })}
          </select>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.contentLayout}>
            {/* 왼쪽: 조직도 트리 */}
            <div className={`${styles.columnContainer} ${styles.treeSection}`}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>조직도</div>
              </div>
              <div className={styles.treeContent}>
                <OrganizationTree
                  companyName={teamList?.[0]?.company || '링크아이'}
                  teamList={sortedTeamList}
                  selectedTeamCode={selectedTeamCode}
                  onSelectTeam={handleTeamSelect}
                  onSelectCompany={handleCompanySelect}
                  isCompanySelected={isCompanySelected}
                  height={510}
                />
              </div>
            </div>

            {/* 가운데: 임직원 목록 */}
            <div className={`${styles.columnContainer} ${styles.employeeSection}`}>
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>임직원</div>
                <div className={styles.searchBlock}>
                  <div className={styles.searchRow}>
                    <div className={styles.searchLabelCell}>이름</div>
                    <div className={styles.searchControlCell}>
                      <input
                        type="text"
                        className={styles.searchInput}
                        style={{ width: '130px' }}
                        value={searchName}
                        onChange={function (e) {
                          setSearchName(e.target.value);
                        }}
                        onKeyDown={function (e) {
                          if (e.key === 'Enter') {
                            handleSearch();
                          }
                        }}
                      />
                      <button type="button" className={styles.smallButton} onClick={handleSearch}>
                        검색
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 직원 목록 테이블 */}
              <div className={styles.employeeTableBlock}>
                <div className={styles.employeeTableSection}>
                  <table className={styles.employeeTable}>
                    <thead>
                      <tr>
                        <th className={styles.thCenter}>
                          <input
                            type="checkbox"
                            checked={
                              displayEmployees.length > 0 &&
                              displayEmployees.every(function (emp) {
                                return checkedEmployees.has(emp.enumber);
                              })
                            }
                            onChange={function (e) {
                              if (e.target.checked) {
                                const employeesToAdd = [];
                                const newChecked = new Set(checkedEmployees);
                                displayEmployees.forEach(function (emp) {
                                  // 이미 체크된 직원은 건너뛰기
                                  if (newChecked.has(emp.enumber)) {
                                    return;
                                  }
                                  // 중복 확인 (selectedEmployees에 이미 있는지)
                                  if (!checkDuplicateEmployee(emp)) {
                                    if (
                                      selectedEmployees.length + employeesToAdd.length <
                                      MAX_SELECTED_EMPLOYEES
                                    ) {
                                      employeesToAdd.push({
                                        enumber: emp.enumber,
                                        name: emp.name,
                                        position: emp.position || '',
                                        team: emp.team || '',
                                        role: '선택',
                                      });
                                      newChecked.add(emp.enumber);
                                    }
                                  } else {
                                    // 이미 selectedEmployees에 있으면 체크만 추가
                                    newChecked.add(emp.enumber);
                                  }
                                });
                                if (employeesToAdd.length > 0) {
                                  setSelectedEmployees(function (prev) {
                                    return [...prev, ...employeesToAdd];
                                  });
                                }
                                setCheckedEmployees(newChecked);
                                if (
                                  employeesToAdd.length < displayEmployees.length &&
                                  selectedEmployees.length + employeesToAdd.length >=
                                    MAX_SELECTED_EMPLOYEES
                                ) {
                                  showAlert('9명 이상 추가할 수 없습니다.');
                                }
                              } else {
                                const uncheckedEnumbers = displayEmployees.map((e) => e.enumber);
                                setSelectedEmployees(function (prev) {
                                  return prev.filter(
                                    (emp) => !uncheckedEnumbers.includes(emp.enumber),
                                  );
                                });
                                setCheckedEmployees(function (prev) {
                                  const newChecked = new Set(prev);
                                  uncheckedEnumbers.forEach(function (enumber) {
                                    newChecked.delete(enumber);
                                  });
                                  return newChecked;
                                });
                              }
                            }}
                          />
                        </th>
                        <th className={styles.thCenter}>부서</th>
                        <th className={styles.thCenter}>직급</th>
                        <th className={styles.thCenter}>이름</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isSearchLoading && showSearchResults ? (
                        <tr>
                          <td colSpan="4" className={styles.emptyCell}>
                            {'검색 중...'}
                          </td>
                        </tr>
                      ) : displayEmployees.length > 0 ? (
                        displayEmployees.map(function (employee) {
                          return (
                            <tr
                              key={employee.enumber}
                              className={styles.employeeRow}
                              draggable
                              onDragStart={function (e) {
                                handleEmployeeDragStart(e, employee);
                              }}
                              onDragEnd={handleEmployeeDragEnd}
                              onClick={function (e) {
                                // 체크박스 클릭은 제외 (onChange에서 처리)
                                if (e.target.type === 'checkbox') {
                                  return;
                                }
                                handleCheckboxToggle(employee);
                              }}
                            >
                              <td className={styles.tdCenter}>
                                <input
                                  type="checkbox"
                                  checked={checkedEmployees.has(employee.enumber)}
                                  onChange={function (e) {
                                    e.stopPropagation();
                                    handleCheckboxToggle(employee);
                                  }}
                                  onClick={function (e) {
                                    e.stopPropagation();
                                  }}
                                />
                              </td>
                              <td className={styles.tdCenter}>{employee.team || '-'}</td>
                              <td className={styles.tdCenter}>{employee.position || '-'}</td>
                              <td className={styles.tdCenter}>{employee.name}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className={styles.emptyCell}>
                            {showSearchResults && !isSearchLoading
                              ? '검색 결과가 없습니다.'
                              : '부서를 선택하거나 검색하세요.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* 오른쪽: 선택된 결재자 카드 목록 */}
            <div
              className={`${styles.columnContainer} ${styles.selectedSection}`}
              onDragOver={handleDragOver}
              onDrop={handleDropToSelectedSection}
            >
              <div className={styles.columnHeader}>
                <div className={styles.columnTitle}>결재자</div>
              </div>
              <div className={styles.selectedList}>
                {selectedEmployees.length > 0 ? (
                  selectedEmployees.map(function (employee, index) {
                    const showTopLine =
                      draggedIndex !== null &&
                      dragOverIndex === index &&
                      dragOverPosition === 'top';
                    const showBottomLine =
                      draggedIndex !== null &&
                      dragOverIndex === index &&
                      dragOverPosition === 'bottom';

                    return (
                      <div key={`${employee.enumber}-${index}`} className={styles.cardWrapper}>
                        {/* 위쪽 파란색 선 */}
                        {showTopLine && <div className={styles.dropIndicator} />}
                        <div
                          className={styles.approverCard}
                          draggable
                          onDragStart={function (e) {
                            handleDragStart(e, index);
                          }}
                          onDragOver={function (e) {
                            handleCardDragOver(e, index);
                          }}
                          onDragLeave={handleCardDragLeave}
                          onDragEnd={handleDragEnd}
                          onDrop={function (e) {
                            handleDrop(e, index);
                          }}
                        >
                          <span className={styles.cardIndex}>{index + 1}</span>
                          <select
                            className={styles.roleDropdown}
                            value={employee.role}
                            onChange={function (e) {
                              handleRoleChange(index, e.target.value);
                            }}
                          >
                            <option value="선택">선택</option>
                            <option value="결재">결재</option>
                            <option value="검토">검토</option>
                            <option value="합의">합의</option>
                          </select>
                          <span className={styles.approverName}>{employee.name}</span>
                          <span className={styles.approverTeam}>{employee.team || '-'}</span>
                          <button
                            type="button"
                            className={styles.removeButton}
                            onClick={function () {
                              handleRemoveEmployee(index);
                            }}
                            aria-label="제거"
                          >
                            ✕
                          </button>
                        </div>
                        {/* 아래쪽 파란색 선 */}
                        {showBottomLine && <div className={styles.dropIndicator} />}
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptyCell}>선택된 직원이 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.btnCancel}`}
            onClick={onClose}
          >
            취소
          </button>
          <button
            type="button"
            className={`${styles.footerButton} ${styles.btnSave}`}
            onClick={handleSave}
          >
            저장
          </button>
        </div>
      </div>

      {/* 공통 Alert 컴포넌트 */}
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
    </div>
  );
}

export default ApprovalLineModal;
